import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseDialogService, NonNullableConfigData, TilbyDialogContentComponent, TilbyDialogToolbarComponent } from '@tilby/tilby-ui-lib/components/tilby-dialog';
import { distinctUntilChanged, filter, lastValueFrom, map, Observable, of, startWith } from 'rxjs';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';

export type ListSelectorOption = { id?: any; name?: string };

export type ListSelectorDialogData<T extends ListSelectorOption = ListSelectorOption> = {
  title?: string;
  searchFieldLabel?: string;
  optionList?: Array<T>;
  optionValue?: T;
  multiSelection?: boolean;
  /** Set to false to hide the search field (default: true) */
  searchable?: boolean;
  hideCancel?: boolean;
}

@Component({
  selector: 'app-list-selector-dialog',
  standalone: true,
  imports: [
          CommonModule,
          MatButtonModule,
          MatDialogModule,
          MatFormFieldModule,
          MatIconModule,
          MatInputModule,
          ReactiveFormsModule,
          TilbyDialogContentComponent,
          TilbyDialogToolbarComponent,
          TranslateModule,
          CdkVirtualScrollViewport,
          ScrollingModule,
          MatRadioModule,
          MatProgressSpinnerModule,
          MatCheckboxModule
  ],
  templateUrl: './list-selector-dialog.component.html',
  styleUrl: './list-selector-dialog.component.scss'
})
export class ListSelectorDialogComponent {

    protected readonly data: ListSelectorDialogData = inject(MAT_DIALOG_DATA);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly fb = inject(FormBuilder);
    private readonly translate = inject(TranslateService);

    filteredOptions: Observable<any[]> = of([]);
    filteredMultiOptions: Observable<AbstractControl<any, any>[]> = of([]);

    form!: FormGroup;
    filterName!: FormControl;

    // Per la selezione tramite radio button
    optionSelected!: FormControl;

    //Per la selezione tramite checkbox
    optionSelectedArray!: FormArray;

    title: string;
    searchFieldLabel: string;
    optionList: Array<any> = [];
    
    calculatedHeight: string = "";
    itemHeight: number = 40;
    visibleItems: number = 10;
    maxVhValueOptionList: number = 50;
    maxViewportHeight: number = this.getMinPixelValue((this.itemHeight * this.visibleItems), this.maxVhValueOptionList);
    viewportHeight$!: Observable<string>;

    constructor() {
      if(this.data?.multiSelection) {
        this.buildFormMultiSelection();
      } else {
        this.buildForm();
      }

      this.title = this.data?.title || "DIALOG.LIST_SELECTOR.TITLE.VALUE";
      this.searchFieldLabel = this.data?.searchFieldLabel || this.translate.instant("DIALOG.LIST_SELECTOR.SEARCH_FIELD.NAME");

      this.optionList = this.data?.optionList || [];

      if(this.data.multiSelection) {
        this.listenToMultiOptionsSearch();
      } else {
        this.listenToOptionsSearch();
      }
    }

    private buildForm() {
      this.filterName = new FormControl('');
      this.optionSelected = new FormControl(this.data?.optionValue? this.data.optionValue.id : null);
      this.form = this.fb.group({
        'filterName': this.filterName,
        'optionSelected': this.optionSelected
      }) 
    }

    private buildFormMultiSelection() {
      this.filterName = new FormControl('');
      this.form = this.fb.group({
        'filterName': this.filterName,
        'options': this.fb.array([])
      });
      this.buildOptionsList();
    }

    get options(): FormArray {
      return this.form.get('options') as FormArray;
    }

    buildOptionsList() {
      if(this.data.optionList?.length) {
        if(this.data.optionList.length > 1) {
          this.options.push(this.fb.group({
            id: [0],
            name: ['Tutte'],
            checked: [false]
          }))
        }
        this.data.optionList?.forEach(opt => {
          this.options.push(this.fb.group({
            id: [opt.id || null],
            name: [opt.name || ''],
            checked: [false]
          }));
        });
      }
    }

    listenToOptionsSearch() {
      this.filteredOptions = this.filterName.valueChanges.pipe(
        startWith(null),
        filter(value => typeof value === 'string' || value === null),
        map(value =>  value? this._filterOptions(value): this.optionList?.slice() || [])
      )

      this.listenToCalculateHeigth(this.filteredOptions);
    }

    listenToMultiOptionsSearch() {
      this.filteredMultiOptions = this.filterName.valueChanges.pipe(
        startWith(null),
        filter(value => typeof value === 'string' || value === null),
        map(value =>  value? this._filterMultiOptions(value): this.options.controls)
      )

      this.listenToCalculateHeigth(this.filteredMultiOptions);
    }

    listenToCalculateHeigth(observable: Observable<any>) {
      this.viewportHeight$ = observable.pipe(
        map(filteredList => {
          let currentLength = filteredList?.length || 0;
          if(this.data.multiSelection) {
            currentLength += 1;
          }
          const calculatedHeight = Math.min(
            this.maxViewportHeight,
            Math.max(currentLength * this.itemHeight)
          );
          return `${calculatedHeight}px`;
        }),
        distinctUntilChanged()
      );
    }
  
    private _filterOptions(query: string): any[] {
      const filterValue = query.toLowerCase();
      
      if(this.optionList) {
        return this.optionList.filter(option => {
          return ((option.name ?? '').toLowerCase().includes(filterValue) ||
          String(option.id ?? '').includes(filterValue))
        }
        );
      } else {
        return [];
      }
    } 

  
    private _filterMultiOptions(query: string): AbstractControl<any, any>[] {
      const filterValue = query.toLowerCase();
      
      if(this.options && this.options.controls) {
        const filteredAbstractControl = this.options.controls.filter((option: AbstractControl) => {
          const value = option.value;
          return value.id == 0 || value?.name.toLowerCase().includes(filterValue) ||
          value?.id.toString().includes(filterValue)
        });
        return filteredAbstractControl;
      } else {
        return [];
      }
    } 

    trackById(index: number, item: any): number | string {
      return item.id;
    }

    trackByControl(index: number, control: AbstractControl): number | string {
      return control.get('id')?.value || index;
    }
    
    checkboxOptionSelectionChange(event: MatCheckboxChange) {
      if(event.source?.value == '0') {
        if(event.checked) {
          this.options.controls.forEach(opt => {
            opt.get('checked')?.setValue(true);
          })
        } else {
          this.options.controls.forEach(opt => {
            opt.get("checked")?.setValue(false);
          })
        }
      } else {
        const allSelected = this.options.value.every((opt: any, index: number) => index == 0 || opt.checked);
        if(allSelected) {
          this.options.at(0).get('checked')?.setValue(true, {emitEvent: false})
        } else {
          this.options.at(0).get('checked')?.setValue(false, {emitEvent: false})
        }
      }
    }

    checkIfDisableSave() {
      if(this.data.multiSelection) {
        return !this.options.value.some((opt: any) => opt.checked)
      } else {
        return this.optionSelected.value === null || this.optionSelected.value === undefined
      }
    }

    getMinPixelValue(pixelValue: number, vhValue: number) {
      const viewportHeightInPixels = window.innerHeight;
      const vhInPixels = (vhValue / 100) * viewportHeightInPixels;
      const min = Math.min(pixelValue, vhInPixels);
      return min;
    }

    onSave(): void {
      if(this.data.multiSelection) {
        let selectedOptions: any = [];
        this.options.value.forEach((opt: any) => {
          if(opt.checked) {
            const valueToAdd = this.data.optionList?.find(option => option.id == opt.id);
            if(valueToAdd) {
              selectedOptions.push(valueToAdd);
            }
          }
        });
        this.matDialogRef?.close(selectedOptions);
      } else {
        this.matDialogRef?.close(this.data?.optionList?.find(opt => opt.id == this.optionSelected?.value))
      }
    }

}

@Injectable({
    providedIn: "root",
})
export class ListSelectorDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public async openDialog<R = any>(config: NonNullableConfigData<ListSelectorDialogData<any>>): Promise<R | undefined> {
      const data: ListSelectorDialogData = config?.data||{ customFilterText: '' };
      return await lastValueFrom(this.dialogRef.open(ListSelectorDialogComponent, {
          ...config,
          ...this.switchMobileDesktopDimensions({ width: "600px" }, {fullScreenForMobile: true}),
          disableClose: true,
          data,
      }).afterClosed()) || undefined;
    }
}
